import { message, Spin, Table } from "antd"
import { useTargetsColumns } from "../hooks/useTargetsColumns"
import { SELECTION_COLUMN_WIDTH, TABLE_SCROLL_X } from "../constants/columns.constants"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { Key, memo, useEffect, useMemo, useRef, useState } from "react"
import { FILTER_TYPES } from "../types/sbTargetApi.types"
import { useActions } from "../../../hooks/useActions"
import { UseTargetsMetrics } from "../hooks/useTargetMetrics"
import { ITargetDataForTable } from "../types/sbTarget.types"
import { setStateType } from "../../../types/setStateType"
import { SponsoredBrandsPlacementsTable } from "./SponsoredBrandsPlacementsTable"
import {
  buildBidAdjustmentItemFromBidding,
  computeDeflatedCPC,
} from "../hooks/helpers/sbPlacementModifier.helper"
import { ISbBidAdjustmentItem } from "../types/sbPlacementApi.types"

interface ISponsoredBrandsTargetsTable {
  campaignId: string
  campaignName: string
  selectedTargetIds: Key[]
  setSelectedTargetIds: setStateType<Key[]>
  selectedRowCampaignIds: Key[]
  setSelectedRowCampaignIds: setStateType<Key[]>
}

const SponsoredBrandsTargetsTableComponent: React.FC<ISponsoredBrandsTargetsTable> = ({
  campaignId,
  campaignName,
  selectedTargetIds,
  setSelectedTargetIds,
  selectedRowCampaignIds,
  setSelectedRowCampaignIds,
}) => {
  const { dateRange, isOrder } = useTypedSelector((select) => select.filter)

  const { targetData, loading, error } = useTypedSelector((select) => select.sponsoredBrandsTarget)
  const { targetDataForTable } = useTypedSelector((select) => select.sponsoredBrandsTargetForTable)
  const { pendingAdjustments, placementData } = useTypedSelector(
    (select) => select.sponsoredBrandsPlacements
  )
  const minTargetCPC = useTypedSelector(
    (select) => select.sponsoredBrands.campaignData.minTargetCPC
  )
  const { fetchSponsoredBrandsTargets, copyTargetsToTargetsForTable, updateTargetsLocal } =
    useActions()

  const [currentTargets, setCurrentTargets] = useState<ITargetDataForTable[] | null>(null)
  const autoSelectedTargetIdsRef = useRef<Set<string>>(new Set())
  const campaignAutoSelectedRef = useRef<boolean>(false)
  const campaignWasSelectedBeforeAutoRef = useRef<boolean>(false)
  const { calcAllMetrics, calcMetricsBeforeUpdatedAcos } = UseTargetsMetrics(campaignName)

  function buildTargetRowKey(
    target: Pick<ITargetDataForTable, "campaignId" | "adGroupId" | "targetId">
  ): string {
    return [target.campaignId || campaignId, target.adGroupId || "", target.targetId]
      .map(String)
      .join(":")
  }

  const campaignTargetData = useMemo(
    () => targetDataForTable.find((target) => target.campaignId === campaignId),
    [targetDataForTable, campaignId]
  )

  useEffect(() => {
    if (loading) {
      return
    }

    if (!targetData || !targetData.length) {
      fetchTargets()
      return
    }

    const targets = targetData.find((target) => target.campaignId === campaignId)
    if (!targets) {
      fetchTargets()
      return
    }

    const currentTargetsWithCalculatedMetrics = calcAllMetrics(targets.targetData)
    copyTargetsToTargetsForTable([
      { campaignId: campaignId, targetData: currentTargetsWithCalculatedMetrics },
    ])
    setCurrentTargets(currentTargetsWithCalculatedMetrics)
  }, [targetData])

  function fetchTargets() {
    fetchSponsoredBrandsTargets({
      fromDate: dateRange.from,
      toDate: dateRange.to,
      campaignIds: [campaignId],
      filterType: isOrder ? FILTER_TYPES.ORDERS : FILTER_TYPES.DATE,
    })
  }

  useEffect(() => {
    if (error) {
      message.error("Failed to load targets")
    }
  }, [error])

  function changeTargetAcos(targetId: string, value: number) {
    const updateTargets =
      currentTargets?.map((target) => {
        if (target.targetId === targetId) {
          return { ...target, targetAcos: value }
        }

        return target
      }) || []

    const currentTargetsWithCalculatedMetrics = calcMetricsBeforeUpdatedAcos(updateTargets)
    setCurrentTargets(currentTargetsWithCalculatedMetrics)
    updateTargetsLocal([
      { campaignId: campaignId, targetData: currentTargetsWithCalculatedMetrics },
    ])
  }

  function changeLimitedCPC(targetId: string, value: number) {
    const updateTargets =
      currentTargets?.map((target) => {
        if (target.targetId === targetId) {
          const updatedTarget = { ...target, limitedCPC: value }
          const rowKey = buildTargetRowKey(updatedTarget)
          setSelectedTargetIds((prev) => Array.from(new Set([...prev, rowKey])))
          return updatedTarget
        }

        return target
      }) || []

    setCurrentTargets(updateTargets)
    updateTargetsLocal([{ campaignId: campaignId, targetData: updateTargets }])
  }

  const columns = useTargetsColumns(changeTargetAcos, changeLimitedCPC)

  // Must equal the parent campaign table's total grid width (TABLE_SCROLL_X).
  const scrollX = TABLE_SCROLL_X

  const campaignBidAdjustment = useMemo(() => {
    const pendingAdjustment = pendingAdjustments[campaignId]
    if (pendingAdjustment) {
      return pendingAdjustment
    }

    const placement = placementData.find((item) => String(item.campaignId) === String(campaignId))
    return placement ? buildBidAdjustmentItemFromBidding(campaignId, placement.bidding) : null
  }, [campaignId, pendingAdjustments, placementData])

  const tableData = useMemo(
    () => findTargetData(campaignBidAdjustment),
    [campaignTargetData, campaignBidAdjustment, minTargetCPC]
  )

  useEffect(() => {
    const autoSelectedTargetIds = tableData
      .filter(shouldAutoSelectDeflatedTarget)
      .map((target) => buildTargetRowKey(target))

    syncAutoSelectedTargets(autoSelectedTargetIds)
    syncAutoSelectedCampaign(autoSelectedTargetIds.length > 0)
  }, [tableData])

  function handleRowSelectionChange(selectedCampaignIds: Key[]) {
    const ownKeys = tableData.map((target) => buildTargetRowKey(target))
    setSelectedTargetIds((prev) => [
      ...prev.filter((id) => !ownKeys.includes(String(id))),
      ...selectedCampaignIds,
    ])
  }

  function syncAutoSelectedTargets(autoSelectedTargetIds: string[]) {
    const nextAutoSelected = new Set(autoSelectedTargetIds)
    const previousAutoSelected = autoSelectedTargetIdsRef.current

    setSelectedTargetIds((prev) => {
      const selected = new Set(prev.map(String))
      previousAutoSelected.forEach((targetId) => {
        if (!nextAutoSelected.has(targetId)) {
          selected.delete(targetId)
        }
      })
      nextAutoSelected.forEach((targetId) => selected.add(targetId))
      const next = Array.from(selected)
      return sameKeySet(prev, next) ? prev : next
    })

    autoSelectedTargetIdsRef.current = nextAutoSelected
  }

  function syncAutoSelectedCampaign(hasAutoSelectedTargets: boolean) {
    const campaignIdString = String(campaignId)

    if (hasAutoSelectedTargets) {
      if (!campaignAutoSelectedRef.current) {
        campaignWasSelectedBeforeAutoRef.current = selectedRowCampaignIds
          .map(String)
          .includes(campaignIdString)
      }
      campaignAutoSelectedRef.current = true
      setSelectedRowCampaignIds((prev) =>
        prev.map(String).includes(campaignIdString) ? prev : [...prev, campaignId]
      )
      return
    }

    if (!campaignAutoSelectedRef.current) {
      return
    }

    campaignAutoSelectedRef.current = false
    if (campaignWasSelectedBeforeAutoRef.current) {
      return
    }

    setSelectedRowCampaignIds((prev) => prev.filter((id) => String(id) !== campaignIdString))
  }

  function findTargetData(bidAdjustment: ISbBidAdjustmentItem | null): ITargetDataForTable[] {
    if (!campaignTargetData) {
      return []
    }

    return dedupeByTargetId(campaignTargetData.targetData)
      .map(withFallbackName)
      .map((target) => ({
        ...target,
        deflatedCPC: computeDeflatedCPC(target.limitedCPC, bidAdjustment, minTargetCPC),
      }))
  }

  function withFallbackName(target: ITargetDataForTable): ITargetDataForTable {
    if (target.targetName && target.targetName.trim()) {
      return target
    }
    return { ...target, targetName: String(target.targetId) }
  }

  function dedupeByTargetId(targets: ITargetDataForTable[]): ITargetDataForTable[] {
    const byTargetId = new Map<string, ITargetDataForTable>()
    for (const target of targets) {
      const key = String(target.targetId)
      const existing = byTargetId.get(key)
      byTargetId.set(key, existing ? mergeTargetRows(existing, target) : target)
    }
    return [...byTargetId.values()]
  }

  function mergeTargetRows(
    base: ITargetDataForTable,
    incoming: ITargetDataForTable
  ): ITargetDataForTable {
    const merged: Record<string, unknown> = { ...base }
    for (const [field, value] of Object.entries(incoming)) {
      if (isEmptyValue(merged[field]) && !isEmptyValue(value)) {
        merged[field] = value
      }
    }
    return merged as unknown as ITargetDataForTable
  }

  function isEmptyValue(value: unknown): boolean {
    return value === undefined || value === null || value === ""
  }

  return !loading ? (
    <>
      <SponsoredBrandsPlacementsTable campaignId={campaignId} />
      {/* Negative margin cancels the 12px horizontal padding antd puts on
          .ant-table-expanded-row > td, so the fixed grid lines up with the
          parent campaign table exactly. */}
      <div style={{ margin: "0 -12px" }}>
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey={(record) => buildTargetRowKey(record)}
          showHeader={false}
          pagination={false}
          virtual={true}
          rowSelection={{
            selectedRowKeys: selectedTargetIds,
            onChange: handleRowSelectionChange,
            columnWidth: SELECTION_COLUMN_WIDTH,
            fixed: true,
          }}
          scroll={{ x: scrollX, y: 360 }}
        />
      </div>
    </>
  ) : (
    <Spin />
  )
}

export const SponsoredBrandsTargetsTable = memo(SponsoredBrandsTargetsTableComponent)

export function shouldAutoSelectDeflatedTarget(
  target: Pick<ITargetDataForTable, "bid" | "deflatedCPC">
): boolean {
  return (
    target.deflatedCPC !== null &&
    Number(target.deflatedCPC.toFixed(2)) !== Number(Number(target.bid ?? 0).toFixed(2))
  )
}

function sameKeySet(left: Key[], right: Key[]): boolean {
  if (left.length !== right.length) {
    return false
  }

  const rightSet = new Set(right.map(String))
  return left.every((key) => rightSet.has(String(key)))
}
